import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box, Text, Flex, Badge, IconButton, Heading,
  Card, CardBody, CardHeader, useColorModeValue, Tooltip,
  HStack, VStack, Spinner, Button, useToast,Switch, FormLabel, FormControl
} from "@chakra-ui/react";
import { MdRefresh, MdFileDownload } from "react-icons/md";
import * as XLSX from "xlsx";
import { TbChecklist } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';
import usageTracker from '../services/usageTracker';


// ── Replace with real apiService import ───────────────────────────────────────
 import apiService from '../services/apiService';

// ── Widths & sticky left offsets ──────────────────────────────────────────────
const W = { client: 185, reload: 104, fail: 110, lbl: 78, col: 160 };
const L = { client: 0, reload: W.client, fail: W.client + W.reload, lbl: W.client + W.reload + W.fail };

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmtDate = d => {
  if (!d) return "—";
  try {
    const [datePart, timePart] = d.split("T");      // split on "T"
    if (!datePart) return "—";

    const [y, m, day] = datePart.split("-").map(Number);
    if (!y || !m || !day || y < 1950) return "N/A";

    const dateStr = `${MONTHS[m - 1]} ${day}, ${y}`;

    if (!timePart) return dateStr;                  // no time → just show date

    // Parse time as-is (strip trailing Z or timezone offset)
    const cleanTime = timePart.replace("Z", "").split(".")[0]; // "15:30:45"
    const [h, min] = cleanTime.split(":").map(Number);
    const ampm  = h >= 12 ? "PM" : "AM";
    const hour  = h % 12 || 12;
    const mins  = String(min).padStart(2, "0");

    return `${dateStr}, ${hour}:${mins} ${ampm}`;   // ✅ "Jan 30, 2026, 3:30 PM" — as-is
  } catch { return "—"; }
};
const fmtExec = d => {
  if (!d) return "—";
  try {
    const dt = new Date(d.replace(" ", "T").split(".")[0] + "Z");
    if (isNaN(dt)) return "—";
    return dt.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return "—"; }
};

const isStale = d => {
  if (!d) return false;
  try {
    const dt = new Date(d.replace(" ", "T").split(".")[0] + "Z");
    const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 2);
    return dt < cutoff;
  } catch { return false; }
};

const is24hOld = d => {
  if (!d) return false;
  try {
    const dt = new Date(d.replace(" ", "T").split(".")[0] + "Z");
    return (Date.now() - dt.getTime()) > 24 * 60 * 60 * 1000;
  } catch { return false; }
};

const failCount = (rd = []) => rd.filter(r => r.status?.toLowerCase() !== "verified").length;

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ChecklistResults() {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [reloading, setReloading] = useState({});


  const [showInternal,   setShowInternal]   = useState(false); // internal clients hidden by default
  const [autoRefresh,    setAutoRefresh]    = useState(true);
  const [countdown,      setCountdown]      = useState(120);   // seconds
  const intervalRef  = useRef(null);
  const countdownRef = useRef(null);

  const AUTO_REFRESH_INTERVAL = 120; 

  const bg       = useColorModeValue("white",   "gray.800");
  const border   = useColorModeValue("#e2e8f0", "#2d3748");
  const headClr  = useColorModeValue("#1a202c", "white");
  const subClr   = useColorModeValue("#4a5568", "#cbd5e0");
  const mutedClr = useColorModeValue("#718096", "#a0aec0");
  const rowEven  = useColorModeValue("#ffffff", "#1a202c");
  const rowOdd   = useColorModeValue("#f7fafc", "#171923");
  const hdrBg    = "#1a3a6b";
  const subHdrBg = "#22508a";
  const thinB    = `1px solid ${border}`;
  const thickB   = "2px solid #94a3b8";

  const overdueBg     = useColorModeValue("#fff5f5", "#2a1a1a"); 
const overdueBorder = "#fc8181";                                  
const overdueText   = useColorModeValue("#c53030", "#fc8181");   
const overdueTimeBg = useColorModeValue("#fed7d7", "#3b1f1f");   

  const toast = useToast();
  const navigate = useNavigate();

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.fetchChecklistResults();
      if (res.success && Array.isArray(res.data)) setData(res.data);
      else setError(res.message || "Failed to load results");
    } catch { setError("Failed to load checklist results"); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

const clearTimers = () => {
  if (intervalRef.current)  clearInterval(intervalRef.current);
  if (countdownRef.current) clearInterval(countdownRef.current);
};

// Start auto-refresh + countdown
const startAutoRefresh = useCallback(() => {
  clearTimers();
  setCountdown(AUTO_REFRESH_INTERVAL);

  // Countdown tick every second
  countdownRef.current = setInterval(() => {
    setCountdown(prev => (prev <= 1 ? AUTO_REFRESH_INTERVAL : prev - 1));
  }, 1000);

  // Fetch every 2 minutes
  intervalRef.current = setInterval(() => {
    fetchData();
    setCountdown(AUTO_REFRESH_INTERVAL);
  }, AUTO_REFRESH_INTERVAL * 1000);
}, [fetchData]);

useEffect(() => {
  if (autoRefresh) startAutoRefresh();
  else { clearTimers(); setCountdown(AUTO_REFRESH_INTERVAL); }
  return () => clearTimers();
}, [autoRefresh, startAutoRefresh]);

  
// Clients actually shown. Internal clients are hidden unless the toggle is on; the
// flag arrives as 1/"1"/true depending on the driver, so accept all three.
const visibleData = useMemo(() => {
    if (showInternal) return data;
    return data.filter(c => {
        const v = c?.isInternal;
        return !(v === 1 || v === "1" || v === true || v === "true");
    });
}, [data, showInternal]);

// ── Replace your existing useMemo ────────────────────────────────────────────
const { columns, featureGroups } = useMemo(() => {
    const seen = new Set(), cols = [], groups = {};
    visibleData.forEach(({ resultData = [] }) => {
        resultData.forEach(({ feature, module }) => {
            const k = `${feature}|||${module}`;
            if (!seen.has(k)) {
                seen.add(k);
                if (!groups[feature]) groups[feature] = [];
                groups[feature].push(module);
                cols.push({ feature, module, key: k });
            }
        });
    });

    // Sort features alphabetically
    const sortedGroups = Object.fromEntries(
        Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
    );

    // Re-order cols to match sorted feature order
    const sortedCols = cols.sort((a, b) => {
        const featCmp = a.feature.localeCompare(b.feature);
        return featCmp !== 0 ? featCmp : a.module.localeCompare(b.module); // modules alphabetically within feature
    });

    return { columns: sortedCols, featureGroups: sortedGroups };
}, [visibleData]);

  
const sortedData = useMemo(() => {
  return [...visibleData].sort((a, b) => b.failureCount - a.failureCount);
}, [visibleData]);


  const getCell = (rd, feat, mod) => (rd || []).find(r => r.feature === feat && r.module === mod);

  // ── Sticky style factories ────────────────────────────────────────────────
  const sHead = (left, w, extra = {}) => ({
    position: "sticky", left, top: 0, zIndex: 12,
    background: hdrBg, color: "white",
    width: w, minWidth: w, maxWidth: w,
    padding: "9px 10px", fontSize: "11px", fontWeight: "700",
    whiteSpace: "nowrap", textAlign: "center",
    borderRight: thinB, boxSizing: "border-box", ...extra,
  });

  const sSub = (left, w, extra = {}) => ({
    position: "sticky", left, top: "35px", zIndex: 11,
    background: subHdrBg, color: "white",
    width: w, minWidth: w, maxWidth: w,
    padding: "6px 10px", fontSize: "11px", fontWeight: "600",
    textAlign: "center", whiteSpace: "nowrap",
    borderRight: thinB, borderBottom: "2px solid rgba(255,255,255,0.25)",
    boxSizing: "border-box", ...extra,
  });

  const sCell = (left, w, rb, extra = {}) => ({
    position: "sticky", left, zIndex: 4,
    background: rb, width: w, minWidth: w,
    padding: "6px 10px", borderRight: thinB,
    boxSizing: "border-box", verticalAlign: "middle", ...extra,
  });

  const dHead = { position: "sticky", top: 0, zIndex: 6, background: hdrBg, color: "white", padding: "9px 10px", fontSize: "11px", fontWeight: "700", textAlign: "center", whiteSpace: "nowrap", borderRight: thinB, boxSizing: "border-box", width: W.col, minWidth: W.col };
  const dSub  = { position: "sticky", top: "35px", zIndex: 6, background: subHdrBg, color: "white", padding: "6px 10px", fontSize: "11px", fontWeight: "500", textAlign: "center", whiteSpace: "nowrap", borderRight: thinB, borderBottom: "2px solid rgba(255,255,255,0.25)", boxSizing: "border-box", width: W.col, minWidth: W.col };

  /**
   * Download one client's checklist results as an .xlsx file.
   *
   * Only checks this client actually has a result for are exported — feature/module
   * columns the client has no cell for are skipped rather than written as
   * "Not Available". The client is identified by the sheet name and file name, so the
   * rows carry just Feature / Module / Date / Status.
   */
  const handleDownloadClientExcel = useCallback((client) => {
    if (!client) return;

    const rows = columns.reduce((acc, col) => {
      const cell = getCell(client.resultData, col.feature, col.module);
      if (!cell) return acc; // no result for this check — omit the row entirely
      // fmtDate renders a missing date as an em-dash for the on-screen table; in a
      // spreadsheet an empty cell is the clearer equivalent.
      const date = fmtDate(cell.date);
      acc.push({
        Feature: col.feature || "",
        Module: col.module || "",
        Date: date === "—" ? "" : date,
        Status: cell.status || "",
      });
      return acc;
    }, []);

    if (!rows.length) {
      toast({
        title: "Nothing to export",
        description: `No checklist results available for ${client.clientName || "this client"}.`,
        status: "info", duration: 4000, isClosable: true, position: "top-right",
      });
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto-width: measure each column by its longest cell value.
    const colKeys = Object.keys(rows[0]);
    ws["!cols"] = colKeys.map(k => ({
      wch: Math.min(40, Math.max(k.length, ...rows.map(r => String(r[k] ?? "").length))),
    }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomRight" };

    const wb = XLSX.utils.book_new();
    // Excel sheet names must be <= 31 chars and exclude : \ / ? * [ ]
    const safeSheetName =
      (client.clientName || "Checklist")
        .replace(/[\\/?*[\]:]/g, " ")
        .trim()
        .slice(0, 31) || "Checklist";
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName);

    const safeFile = (client.clientName || "client").replace(/[^\w.-]+/g, "_");
    XLSX.writeFile(wb, `${safeFile}_checklist_results.xlsx`);
    usageTracker.action('Checklist Results', 'EXPORT_EXCEL');
  }, [columns, toast]);

 const handleReload = async (clientId, clientName) => {
  setReloading(p => ({ ...p, [clientId]: true }));
console.log("handlinggg")
  // Trigger toast — shown immediately on click
  toast({
    title: "Checklist Execution Triggered",
    description: `Checklist report for "${clientName}" will execute and retry within 2 mins.`,
    status: "info",
    duration: 5000,
    isClosable: true,
    position: "top-right",
  });

  try {
    const res = await apiService.reloadChecklistClient(clientId);

    if (res.success) {
        usageTracker.action('Checklist Results', 'RELOAD');
      toast({
        title: "Reload Successful",
        description: `"${clientName}" checklist has been queued for re-execution.`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } else {
      toast({
        title: "Reload Failed",
        description: res.message || `Failed to reload "${clientName}". Please try again.`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  } catch {
    toast({
      title: "Reload Error",
      description: `An unexpected error occurred while reloading "${clientName}".`,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  } finally {
    setReloading(p => ({ ...p, [clientId]: false }));
  }
};

const handleClientNameClick= useCallback((clientId, clientName) => {
        try {
            const params = new URLSearchParams();
            params.set('clientId', clientId.toString());
            params.set('clientName', clientName);
            const mappingsPath = `/checklist-mappings?${params.toString()}`
            //const issuesPath = `/issues?${params.toString()}`;
            navigate(mappingsPath);
        } catch (error) {
            console.error('Failed to navigate to Client Profile:', error);
            
        }
    }, [navigate]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box p={4}>
      <VStack spacing={5} align="stretch">

        {/* Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg" color={headClr}>Checklist Results</Heading>
            <Text color={subClr} mt={1} fontSize="sm">
              Latest execution results across all mapped clients
            </Text>
          </Box>

          <HStack spacing={2} wrap="wrap">
            {/* Show Internal toggle — internal clients are hidden by default */}
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch
                id="show-internal"
                colorScheme="blue"
                isChecked={showInternal}
                onChange={e => setShowInternal(e.target.checked)}
              />
              <FormLabel htmlFor="show-internal" mb={0} ml={2} fontSize="sm" color={subClr} cursor="pointer">
                Show Internal
              </FormLabel>
            </FormControl>

            {/* Auto-refresh toggle */}
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch
                id="auto-refresh"
                colorScheme="blue"
                isChecked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
              />
              <FormLabel htmlFor="auto-refresh" mb={0} fontSize="sm" color={subClr} cursor="pointer">
                Auto Refresh
                {/* {autoRefresh && (
                  <Badge ml={2} colorScheme="blue" variant="subtle" fontSize="xs">
                    {countdown}s
                  </Badge>
                )} */}
              </FormLabel>
            </FormControl>

            {/* Manual refresh */}
            <Button
              leftIcon={<MdRefresh />}
              onClick={fetchData}
              variant="outline"
              size="sm"
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {/* Loading */}
        {loading && (
          <Flex justify="center" py={16}>
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color={subClr}>Loading checklist results...</Text>
            </VStack>
          </Flex>
        )}

        {/* Error */}
        {!loading && error && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color="red.400"><TbChecklist size={40} /></Box>
              <Text color="red.500" fontWeight="semibold">{error}</Text>
              <Button size="sm" onClick={fetchData} colorScheme="blue" variant="outline">Retry</Button>
            </VStack>
          </Flex>
        )}

        {/* Empty */}
        {!loading && !error && data.length === 0 && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color={mutedClr}><TbChecklist size={44} /></Box>
              <Text color={mutedClr} fontSize="lg">No results found</Text>
            </VStack>
          </Flex>
        )}

        {/* Grid */}
        {!loading && !error && data.length > 0 && (
          <Card bg={bg} borderColor={border} borderWidth="1px" overflow="hidden">
            <CardHeader pb={2} pt={4} px={4}>
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Box color="blue.500"><TbChecklist size={20} /></Box>
                  <Heading size="md" color={headClr}>Execution Results</Heading>
                </HStack>
                <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1}>
                  {data.length} client{data.length !== 1 ? "s" : ""}
                </Badge>
              </Flex>
            </CardHeader>

            <CardBody p={0}>
              <Box overflowX="auto" overflowY="auto" maxH="72vh"
                sx={{ "&::-webkit-scrollbar": { h: "8px", w: "8px" }, "&::-webkit-scrollbar-thumb": { bg: "gray.300", borderRadius: "4px" } }}>
                <table style={{ borderCollapse: "collapse", tableLayout: "fixed",
                  minWidth: `${W.client + W.reload + W.fail + W.lbl + columns.length * W.col}px` }}>
                  <thead>
                    {/* Row 1 – Feature group names */}
                    <tr>
                      <th style={sHead(L.client,  W.client,  { borderRight: thinB })}  rowSpan={2}>Client</th>
                      <th style={sHead(L.reload,  W.reload)}                           rowSpan={2}>Actions</th>
                      <th style={sHead(L.fail,    W.fail)}                             rowSpan={2}>Failure Count</th>
                      <th style={sHead(L.lbl,     W.lbl,     { borderRight: thickB })} rowSpan={2}></th>
                      {Object.entries(featureGroups).map(([feat, mods]) => (
                        <th key={feat} colSpan={mods.length}
                          style={{ ...dHead, minWidth: mods.length * W.col, width: mods.length * W.col,
                            borderLeft: "1px solid rgba(255,255,255,0.18)" }}>
                          {feat}
                        </th>
                      ))}
                    </tr>
                    {/* Row 2 – Module names */}
                    <tr>
                      {columns.map((col, i) => (
                        <th key={col.key} style={{ ...dSub, borderLeft: i === 0 ? thickB : "1px solid rgba(255,255,255,0.12)" }}>
                          {col.module}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {sortedData.map((client, idx) => {
                      const fc    = client.failureCount;
                      const stale = isStale(client.lastExecuted);
                      const overdue = is24hOld(client.lastExecuted); 
                      const rb    = idx % 2 === 0 ? rowEven : rowOdd;
                      const rowBot = "2px solid #94a3b8";
                      const dashBot = `1px dashed ${border}`;

                      return [
                        /* Date row */
                        <tr key={`${client.clientId}-d`}>
                          <td
                            style={sCell(L.client, W.client, overdue ? overdueBg : rb, {
                              borderBottom: dashBot,
                              borderRight: thinB,
                              borderLeft: overdue ? `4px solid ${overdueBorder}` : "4px solid transparent",
                            })}
                            rowSpan={2}
                            onClick={() => handleClientNameClick(client.clientId, client.clientName)}
                          >
                            <div style={{ paddingLeft: 2, cursor: "pointer" }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: headClr }}>
                                {client.clientName}
                              </div>
                              <div style={{
                                fontSize: 11, marginTop: 3, display: "inline-block", borderRadius: 3,
                                padding: (stale || overdue) ? "1px 5px" : 0,
                                color: stale ? overdueText : overdue ? overdueText : mutedClr,
                                background: stale ? overdueTimeBg : overdue ? overdueTimeBg : "transparent",
                              }}>
                                {fmtExec(client.lastExecuted)}
                              </div>
                              {/* {overdue && (
                                <div style={{ fontSize: 10, color: overdueText, fontWeight: 600, marginTop: 2 }}>
                                  ⚠ Last run &gt;24h ago
                                </div>
                              )} */}
                            </div>
                          </td>
                          <td style={sCell(L.reload, W.reload, rb, { textAlign: "center", borderBottom: dashBot })} rowSpan={2}>
                            <HStack spacing={0} justify="center">
                              <Tooltip label="Reload client data">
                                <IconButton
                                  icon={<MdRefresh />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="reload"
                                  isLoading={!!reloading[client.clientId]}
                                  onClick={() => handleReload(client.clientId, client.clientName)}
                                />
                              </Tooltip>
                              <Tooltip label={`Download ${client.clientName || "client"} results as Excel`}>
                                <IconButton
                                  icon={<MdFileDownload />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="green"
                                  aria-label={`Download ${client.clientName || "client"} checklist results`}
                                  onClick={() => handleDownloadClientExcel(client)}
                                />
                              </Tooltip>
                            </HStack>
                          </td>
                          <td style={sCell(L.fail, W.fail, rb, { textAlign: "center", borderBottom: dashBot })} rowSpan={2}>
                            <Badge fontSize="sm" px={2} py={1} borderRadius="md"
                              colorScheme={fc === 0 ? "green" : fc > 5 ? "red" : "orange"}>
                              {fc}
                            </Badge>
                          </td>
                          <td style={sCell(L.lbl, W.lbl, rb, { textAlign: "center", fontSize: 11, fontWeight: 700, color: mutedClr,
                            letterSpacing: "0.05em", textTransform: "uppercase", borderRight: thickB, borderBottom: dashBot })}>
                            Date
                          </td>
                          {columns.map((col, i) => {
                            const cell = getCell(client.resultData, col.feature, col.module);
                            return (
                              <td key={col.key} style={{ width: W.col, minWidth: W.col, padding: "6px 10px", fontSize: 11,
                                color: subClr, textAlign: "center", background: rb,
                                borderLeft: i === 0 ? thickB : thinB, borderBottom: dashBot, boxSizing: "border-box" }}>
                                {cell ? fmtDate(cell.date) : <span style={{ color: mutedClr, fontSize: 13 }}>—</span>}
                              </td>
                            );
                          })}
                        </tr>,

                        /* Status row */
                        <tr key={`${client.clientId}-s`}>
                          <td style={sCell(L.lbl, W.lbl, rb, { textAlign: "center", fontSize: 11, fontWeight: 700, color: mutedClr,
                            letterSpacing: "0.05em", textTransform: "uppercase", borderRight: thickB, borderBottom: rowBot })}>
                            Status
                          </td>
                          {columns.map((col, i) => {
                            const cell = getCell(client.resultData, col.feature, col.module);
                            const ok   = cell?.status?.toLowerCase() === "verified";
                            return (
                              <td key={col.key} style={{ width: W.col, minWidth: W.col, padding: "6px 10px", textAlign: "center",
                                background: rb, borderLeft: i === 0 ? thickB : thinB, borderBottom: rowBot, boxSizing: "border-box" }}>
                                {cell
                                  ? <span style={{ display: "inline-block", fontSize: 11, fontWeight: 600, borderRadius: 4, padding: "2px 8px",
                                      background: ok ? "#c6f6d5" : "#feebc8", color: ok ? "#276749" : "#7b341e" }}>
                                      {cell.status}
                                    </span>
                                  : <span style={{ color: mutedClr, fontSize: 13 }}>—</span>
                                }
                              </td>
                            );
                          })}
                        </tr>
                      ];
                    })}
                  </tbody>
                </table>
              </Box>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
}